import isEmpty from 'lodash/isEmpty'
import setWith from 'lodash/setWith'

import { PLATFORM, isPlatform } from '../../utils/enums'
import { isValidPropertyString } from '../../utils/stringValidator'
import { getAccountData, setAccountData, useAccountData } from '../accountData'
import { isEnabledPlatform } from '../enabledPlatforms'

export type PlatformUserAccountData = {
  /**
   * Used only for Instagram
   */
  accountType?: string

  /**
   * Used only for Instagram
   */
  accountVisibility?: string

  /**
   * Internal; used server-side to identify completed playbooks
   */
  isCompleted?: boolean

  /**
   * Internal; indicates if this data was deleted; helps with sync'ing
   */
  isDeleted?: boolean

  lastUpdatedTimestamp: number

  /**
   * Internal; used server-side to track checklist progress
   */
  nChecklistRemaining?: number

  /**
   * [DEPRECATED] Previously used only for Facebook; waiting to delete
   */
  profileUrl?: string

  /**
   * Timestamp the user started interacting with the platform. Used server-side
   * to schedule autoscans.
   */
  started?: number

  /**
   * Username for display purposes, e.g. on the Dashboard
   */
  username?: string
}

type PlatformUserAccountDataSet = Omit<
  PlatformUserAccountData,
  'lastUpdatedTimestamp'
>

type PlatformUserAccountDataUpdate = Omit<
  PlatformUserAccountData,
  'lastUpdatedTimestamp'
>

export async function getPlatformUserAccountData(
  platform: PLATFORM | null | undefined,
  platformUserId: string | undefined,
): Promise<PlatformUserAccountData | null | undefined> {
  if (platform === undefined || platformUserId === undefined) {
    return undefined
  }
  if (platform === null) {
    return null
  }
  const accountData = await getAccountData()
  return accountData[platform]?.[platformUserId] || null
}

export type PlatformUserIdPairs = [PLATFORM, string][]

export async function getPlatformUserIdPairs(): Promise<PlatformUserIdPairs> {
  const accountData = await getAccountData()

  const platformUserIdPairs: PlatformUserIdPairs = []
  if (accountData) {
    Object.keys(accountData).forEach((platform) => {
      Object.keys(accountData[platform]).forEach((platformUserId) => {
        if (isPlatform(platform)) {
          platformUserIdPairs.push([platform, platformUserId])
        }
      })
    })
  }

  return platformUserIdPairs
}

type SetPlatformUserAccountDataOptions = {
  delete?: boolean
}

export async function setPlatformUserAccountData(
  platform: PLATFORM,
  platformUserId: string,
  platformUserAccountDataSet: PlatformUserAccountDataSet,
  options: SetPlatformUserAccountDataOptions = {},
): Promise<void> {
  if (!isEnabledPlatform(platform)) {
    throw new Error(`setPlatformUserAccountData: Invalid platform ${platform}`)
  }
  if (!isValidPropertyString(platformUserId)) {
    throw new Error(
      `setPlatformUserAccountData: Invalid platform user ID ${platformUserId}`,
    )
  }

  let accountData = await getAccountData()

  if (options.delete) {
    accountData = setWith(
      accountData,
      [platform, platformUserId],
      {
        isDeleted: true,
      },
      Object,
    )
  } else {
    const update: PlatformUserAccountDataSet = {
      ...platformUserAccountDataSet,
      isDeleted: false,
    }

    accountData = setWith(
      accountData,
      [platform, platformUserId],
      update,
      Object,
    )
  }

  // Always update timestamp
  accountData = setWith(
    accountData,
    [platform, platformUserId, 'lastUpdatedTimestamp'],
    Date.now(),
    Object,
  )

  return setAccountData(accountData)
}

export async function updatePlatformUserAccountData(
  platform: PLATFORM,
  platformUserId: string,
  incomingPlatformUserAccountDataUpdate: PlatformUserAccountDataUpdate,
): Promise<void> {
  const platformUserAccountDataUpdate = incomingPlatformUserAccountDataUpdate

  const platformUserAccountData = await getPlatformUserAccountData(
    platform,
    platformUserId,
  )

  // Add a started timestamp if there isn't one. This is so we know the first
  // time this platform+platform user ID was interacted with. This value is
  // used server-side to schedule autoscans.
  if (!platformUserAccountData?.started) {
    platformUserAccountDataUpdate.started = Date.now()
  }

  return setPlatformUserAccountData(platform, platformUserId, {
    ...(platformUserAccountData || {}),
    ...platformUserAccountDataUpdate,
  })
}

export async function initPlatformUserAccountData(
  platform: PLATFORM,
  platformUserId: string,
): Promise<void> {
  const platformUserAccountData = await getPlatformUserAccountData(
    platform,
    platformUserId,
  )
  // TODO: This isn't the best check; better would be to define the expected
  // attributes and create a validator for that.
  if (isEmpty(platformUserAccountData)) {
    await setPlatformUserAccountData(platform, platformUserId, {
      isDeleted: false,
      isCompleted: false,
      started: Date.now(),
    })
  }
}

export async function setPlatformUserAccountDataChecklistProgress(
  platform: PLATFORM,
  platformUserId: string,
  isCompleted: boolean,
  nChecklistRemaining: number,
) {
  return updatePlatformUserAccountData(platform, platformUserId, {
    isCompleted,
    nChecklistRemaining,
  })
}

export function usePlatformUserAccountData(
  platform: PLATFORM | null | undefined,
  platformUserId: string | undefined,
): PlatformUserAccountData | null | undefined {
  const accountData = useAccountData()
  if (!accountData) {
    return undefined
  }
  if (platform === undefined || platformUserId === undefined) {
    return undefined
  }
  if (platform === null) {
    return null
  }
  return accountData[platform]?.[platformUserId] || null
}
